import {
  CreateUpsellDTO,
  DeleteUpsellDTO,
  UpdateUpsellDTO
} from '@/@types/Upsell';
import { FUNNEL_QUERY_KEY } from '@/hooks/useFunnelData';
import { UpsellService } from '@/services/upsell.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useUpsells(
  productId: string,
  options?: { page?: number; perPage?: number },
) {
  const queryClient = useQueryClient();
  const baseKey = ['upsells', productId];
  const queryKey = [...baseKey, options?.page ?? 1, options?.perPage];

  const {
    data: upsells,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () =>
      await UpsellService.listUpsells(productId, {
        page: options?.page,
        perPage: options?.perPage,
      }),
  });

  const { data: upsell, isLoading: isLoadingUpsell } = useQuery({
    queryKey: [...baseKey, 'detail'],
    queryFn: async ({ queryKey: qk }) => {
      const upsellId = qk[2] as string | undefined;
      if (!upsellId) return null;
      const response = await UpsellService.listUpsell(productId, upsellId);
      return response;
    },
    enabled: false,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: baseKey });
    queryClient.invalidateQueries({ queryKey: [FUNNEL_QUERY_KEY, productId] });
  };

  const createMutation = useMutation({
    mutationFn: async ({ productId, data }: CreateUpsellDTO) => {
      const response = await UpsellService.createUpsell({
        productId,
        data,
      });
      return response;
    },
    onSuccess: invalidateAll,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ productId, upsellId, data }: UpdateUpsellDTO) => {
      const response = await UpsellService.updateUpsell({
        productId,
        upsellId,
        data,
      });
      return response;
    },
    onSuccess: invalidateAll,
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ productId, upsellId }: DeleteUpsellDTO) => {
      await UpsellService.deleteUpsell({
        productId,
        upsellId,
      });
    },
    onSuccess: invalidateAll,
  });

  return {
    upsells,
    upsell,
    isLoading,
    isLoadingUpsell,
    error,
    createUpsell: createMutation.mutateAsync,
    updateUpsell: updateMutation.mutateAsync,
    deleteUpsell: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
